<template>
  <page-loader>
    <v-container>
      <filter-accordion
        :loading="isLoading"
        :query-changed="$_queryHandler_hasQueryChanged"
        :query-count="$_queryHandler_numberOfQueries"
        @clear-filters="$_queryHandler_resetFilters"
      >
        <template #default>
          <v-row>
            <v-col v-for="(option, key) in filtersOptions" :key="key" sm="4">
              <select-list
                v-model="query.params[option.query]"
                :items="option.items"
                :label="option.label"
                clearable
                hide-details
                outlined
                :custom-type="option.customType"
                :limit="1"
                :multiple="option.multiple"
              />
            </v-col>
            <v-col sm="4" style="display: flex; align-items: center;">
              <toggle-input
                v-model="query.params.automatic"
                :disabled="isLoading"
                label="Show Global Only"
                hide-details
                outlined
              />
            </v-col>
          </v-row>
        </template>
        <template #content>
          <rule-table
            :items="filteredRules"
            disable-sort
            :items-per-page="30"
            :offer-types="offerTypes"
            :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, -1] }"
            @pagination="$_queryHandler_onUpdatePagination"
            @rule-edit="onSelectRule"
            @rule-delete="onDeleteRule"
          />
        </template>
      </filter-accordion>
    </v-container>

    <generic-modal
      v-model="showCreateModal"
      :is-disabled="isLoading || !newRule.type || !newRule.partner"
      :is-loading="isLoading"
      confirm-text="Create Rule"
      title="Create a traffic rule"
      @modal-confirm="onCreateModalConfirm"
      @modal-cancel="onCreateModalCancel"
    >
      <rule-form
        v-model="newRule"
        :has-errors="hasPageErrors"
        :partners="partners"
        :offers="offers"
        :sites="sites"
        :offer-types="offerTypes"
        :site-types="siteTypes"
        :languages="languages"
      />
    </generic-modal>

    <generic-modal
      v-model="showEditModal"
      :is-disabled="!isPermittedTo('update')"
      :is-loading="isLoading"
      confirm-text="Edit Rule"
      title="Update a Traffic Rule"
      @modal-confirm="onEditModalConfirm"
      @modal-cancel="onEditModalCancel"
    >
      <rule-form
        v-model="currentRule"
        :has-errors="hasPageErrors"
        :is-disabled="!isPermittedTo('update')"
        :partners="partners"
        :offers="offers"
        :sites="sites"
        :offer-types="offerTypes"
        :site-types="siteTypes"
        :languages="languages"
      />
    </generic-modal>

    <delete-modal
      v-if="currentRule && showDeleteModal"
      v-model="showDeleteModal"
      item="this rule"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="Rule"
      @modal-confirm="onModalDeleteConfirm"
      @modal-cancel="onModalDeleteCancel"
    />

    <delete-modal
      v-if="showBulkDeleteModal"
      v-model="showBulkDeleteModal"
      :item="bulkDeleteModalText"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="Traffic Rules"
      hide-louis
      @modal-confirm="onModalBulkDeleteConfirm"
      @modal-cancel="onModalBulkDeleteCancel"
    >
      <div>
        <div class="mt-2 mb-1">Traffic Rules matching the following filters will be deleted:</div>
        <modal-filter-list
          v-for="(option, key) in filteredFiltersOptions"
          :key="key"
          :items="option.items"
          :value="query.params[option.query]"
          :query-param="option.query"
        />
      </div>
      <v-sheet class="mt-3 overflow-auto" max-height="300">
        <rule-table
          :items="filteredRules"
          disable-sort
          :items-per-page="30"
          :offer-types="offerTypes"
          :footer-props="{ 'items-per-page-options': [-1] }"
          :ignore-columns="['priority', 'percentage', 'actions']"
          @pagination="$_queryHandler_onUpdatePagination"
          @rule-edit="onSelectRule"
          @rule-delete="onDeleteRule"
        />
      </v-sheet>
    </delete-modal>

    <fab-actions
      :can-create="isPermittedTo('create')"
      :can-delete="isPermittedTo('delete') && !!filteredRules.length"
      :labels="{ delete: 'Bulk delete rules' }"
      context="Rule"
      @create="showCreateModal = true"
      @delete="showBulkDeleteModal = true"
    />
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import setPlural from '@/utils/set-plural'
import getOptionKeyByValue from '@/utils/get-option-key-by-value'

import FabActions from '@/components/fab-actions'
import DeleteModal from '@/components/modals/delete-modal'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import FilterAccordion from '@/components/filter-accordion'
import ToggleInput from '@/components/forms/toggle-input'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { partners, offers, trafficRules, sites } from '@/services/offers'

import RuleForm from '@/views/traffic/components/rule-form'
import RuleTable from '@/views/traffic/components/rule-table'
import ModalFilterList from '@/views/traffic/components/modal-filter-list'
import SelectList from '@/components/forms/select-list'

const newRuleGenerator = ({
  type = null,
  partner = null,
  geo = null,
  offer = null,
  site = null,
  percentage = null,
  priority = null,
} = {}) => ({
  type,
  partner,
  geo,
  offer,
  site,
  percentage,
  priority,
})

export default {
  name: 'TrafficManagement',
  components: {
    DeleteModal,
    FabActions,
    GenericModal,
    PageLoader,
    RuleForm,
    SelectList,
    FilterAccordion,
    RuleTable,
    ModalFilterList,
    ToggleInput,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    showCreateModal: false,
    showEditModal: false,
    showDeleteModal: false,
    showBulkDeleteModal: false,
    newRule: newRuleGenerator(),
    currentRule: newRuleGenerator(),
    partners: [],
    offers: [],
    sites: [],
    rules: [],
    defaultQuery: {
      rules: ['receive', 'block', 'fullBlock'],
    },
    queriesDefined: [
      'country',
      'language',
      'offerType',
      'offer',
      'site',
      'partner',
      'rules',
      'searchTerm',
      'siteType',
      'automatic',
    ],
    queryDefaults: ['rules'],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    filtersOptions() {
      const trafficRules = [
        { key: 'Direct', value: 'receive' },
        { key: 'Limit', value: 'block' },
        { key: 'Block', value: 'fullBlock' },
      ]
      return [
        { label: 'Offer Type', query: 'offerType', items: this.offerTypes },
        { label: 'Partner', query: 'partner', items: this.mappedPartners, customType: 'avatar' },
        { label: 'Offer', query: 'offer', items: this.mappedOffers },
        { label: 'Site', query: 'site', items: this.mappedSites },
        { label: 'Country', query: 'country', items: this.countries, customType: 'country' },
        { label: 'Language', query: 'language', items: this.languages },
        { label: 'Site Type', query: 'siteType', items: this.siteTypes },
        { label: 'Rules', query: 'rules', items: trafficRules, multiple: true },
      ]
    },
    filteredFiltersOptions() {
      const filteredQueryObject = Object.entries(this.query.params)
        .filter(([k, v]) => v)
        .map(([k]) => k)
      return this.filtersOptions.filter(({ query }) => filteredQueryObject.includes(query))
    },
    countries() {
      return this.getItemsForOption('countries').concat([{ key: 'Worldwide', value: null }])
    },
    offerTypes() {
      return [{ key: 'No Offer Type', value: null }, ...this.getItemsForOption('offerTypes')]
    },
    siteTypes() {
      return this.getItemsForOption('siteTypes')
    },
    mappedSites() {
      return this.sites.map(({ name, hostname }) => ({
        key: name || hostname,
        value: hostname,
      }))
    },
    languages() {
      return this.getItemsForOption('languages')
    },
    mappedOffers() {
      return this.offers.map(({ name, slug }) => ({
        key: name || slug,
        value: slug,
      }))
    },
    mappedPartners() {
      return this.partners.map(({ name, slug, logoThumbnail }) => ({
        key: name || slug,
        value: slug,
        icon: logoThumbnail,
      }))
    },
    rulesWithMeta() {
      return this.rules.map(rule => ({
        ...rule,
        meta: {
          partner: this.nameForPartner(rule.partner),
          offer: this.nameForOffer(rule.offer),
          site: this.nameForSite(rule.site),
          country: this.nameForCountryCode(rule.geo) || 'Worldwide',
          language: this.formattedOption('languages', rule.language),
          siteType: this.formattedOption('siteTypes', rule.siteType),
        },
      }))
    },
    filteredRules() {
      return this.rulesWithMeta
        .filter(rule => {
          const { country, language, offerType, offer, site, partner, rules, siteType, automatic } = this.query.params
          console.log(!automatic || rule.automatic)
          return (
            (!country || country === rule.geo) &&
            (!language || language === rule.language) &&
            (offerType === undefined || rule.offerType === offerType) &&
            (!offer || offer === rule.offer) &&
            (!site || site === rule.site) &&
            (!partner || partner === rule.partner) &&
            (!rules || rules.includes(rule.type)) &&
            (!siteType || siteType === rule.siteType) &&
            (!automatic || rule.automatic)
          )
        })
        .sort((a, b) => {
          const typeOrder = ['receive', 'block']
          return (
            typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type) ||
            Number(!!b.offer) - Number(!!a.offer) ||
            (a.geo === b.geo ? 0 : a.geo > b.geo ? 1 : -1) ||
            a.priority - b.priority ||
            b.percentage - a.percentage ||
            b.id - a.id
          )
        })
    },
    bulkDeleteModalText() {
      return `${this.filteredRules.length} ${setPlural(this.filteredRules.length, 'Traffic Rule')}`
    },
  },
  async created() {
    try {
      const [ruleData, partnerData, offerData, sitesData] = await Promise.all([
        trafficRules.getTrafficRules(),
        partners.getAllPartners({ compact: true }),
        offers.getAllOffers({ compact: true }),
        sites.getAllSites({ compact: true }),
      ])
      this.rules = ruleData
      this.offers = offerData.data
      this.partners = partnerData.data
      this.sites = sitesData.data
    } catch (error) {
      this.setPageErrors(true)
      this.$notify({
        type: 'error',
        text: 'Unable to fetch traffic rules',
      })
    }

    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    setPlural,
    getOptionKeyByValue,
    nameForCountryCode(countryCode) {
      return (this.countries.find(c => c.value === countryCode) || {}).key || ''
    },
    nameForPartner(slug) {
      return (this.partners.find(p => p.slug === slug) || {}).name || slug
    },
    nameForOffer(slug) {
      return (this.offers.find(o => o.slug === slug) || {}).name || slug
    },
    nameForSite(hostname) {
      return (this.sites.find(o => o.hostname === hostname) || {}).name || hostname
    },
    async onCreateModalConfirm() {
      this.showCreateModal = false
      if (!this.newRule) return
      this.hasErrors = false
      this.isLoading = true
      try {
        const { id } = await trafficRules.createTrafficRule(this.newRule)
        this.$set(this, 'rules', await trafficRules.getTrafficRules())
      } catch (error) {
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Unable to create new traffic rule'
        this.hasErrors = true
        this.$notify({
          type: 'error',
          text: errorMessage,
        })
      }
      this.isLoading = false
    },
    onCreateModalCancel() {
      this.newRule = newRuleGenerator()
    },
    async onEditModalConfirm() {
      this.showEditModal = false
      if (!this.currentRule) return
      this.hasErrors = false
      this.isLoading = true
      try {
        await trafficRules.updateTrafficRule(this.currentRule.id, this.currentRule)
        this.$set(this, 'rules', await trafficRules.getTrafficRules())
      } catch (error) {
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Unable to update traffic rule'
        this.hasErrors = true
        this.$notify({
          type: 'error',
          text: errorMessage,
        })
      }
      this.isLoading = false
    },
    onEditModalCancel() {
      this.currentRule = newRuleGenerator()
    },
    onSelectRule(rule) {
      this.currentRule = rule
      this.showEditModal = true
    },
    onDeleteRule(rule) {
      this.currentRule = rule
      this.showDeleteModal = true
    },
    async onModalDeleteConfirm() {
      this.showDeleteModal = false
      if (!this.currentRule.id) return
      this.hasErrors = false
      this.isLoading = true
      try {
        await trafficRules.deleteTrafficRule(this.currentRule.id)
        this.$set(this, 'rules', await trafficRules.getTrafficRules())
        this.currentRule = newRuleGenerator()
      } catch (error) {
        this.hasErrors = true
        this.$notify({
          type: 'error',
          text: 'Unable to delete traffic rule',
        })
      }
      this.isLoading = false
    },
    onModalDeleteCancel() {
      this.showDeleteModal = false
      this.currentRule = newRuleGenerator()
    },
    async onModalBulkDeleteConfirm() {
      this.showBulkDeleteModal = false
      this.hasErrors = false
      this.isLoading = true

      try {
        await trafficRules.deleteFilteredTrafficRules({ filters: this.queryFilters })
        this.$set(this, 'rules', await trafficRules.getTrafficRules())
      } catch (error) {
        this.hasErrors = true
        this.$notify({
          type: 'error',
          text: 'Unable to delete traffic rules',
        })
      }
      this.isLoading = false
    },
    onModalBulkDeleteCancel() {
      this.showDeleteModal = false
    },
  },
}
</script>
